import { Injectable, OnDestroy, inject } from '@angular/core';
import { Internal } from '../../decorators/app.decorators';
import { Store } from '@ngxs/store';
import { jwtDecode } from 'jwt-decode';
import { AuthState } from '../../../store/auth/auth.state';
import { GenerateRefreshToken } from '../../../store/auth/auth.actions';
import { Subscription, switchMap, timer } from 'rxjs';
import { SubSink } from 'subsink';
import { AuthService } from '../auth/auth.service';

@Internal()
@Injectable({
  providedIn: 'root'
})
export class UserSessionManagementService implements OnDestroy{

  subs: SubSink = new SubSink();
  private authService = inject(AuthService);
  /** Current re-arm timer chain; replaced (not accumulated) every cycle - see setAutoRefreshToken. */
  private refreshTimerSub: Subscription | null = null;

  constructor(private store: Store) {
    this.subs.sink = this.store.select(AuthState.isTokenExpired).subscribe((isSessionExpired) => {
          if (!isSessionExpired) {
            this.setAutoRefreshToken();
          } else {
            // Logout (or a failed refresh) clears the token: stop the re-arm
            // chain so a timer from the old session can't fire after re-login.
            this.refreshTimerSub?.unsubscribe();
            this.refreshTimerSub = null;
          }
        })
   }
  ngOnDestroy(): void {
    console.log("destroying"+this);

    this.subs.unsubscribe();
    this.refreshTimerSub?.unsubscribe();
  }


    /**
     * AutoRefresh will keep the 'access token' valid until it reaches end of life.
     *
     * Re-arms itself after every successful refresh so it keeps recurring for
     * the whole session, not just once. It can't rely on re-subscribing to
     * AuthState.isTokenExpired for that: that selector is `!accessToken`,
     * which is false both before and after a successful refresh, so NGXS's
     * distinctUntilChanged never re-emits and a subscription driven by it
     * only ever fires the first time. That's why a user idle on a page for
     * longer than one token lifetime used to see their session lapse with no
     * further silent renewal.
     *
     * The chain lives in `refreshTimerSub`, not `subs`: each cycle replaces
     * the previous subscription instead of appending to `subs`, which
     * otherwise grows unboundedly (this is a root-singleton service whose
     * ngOnDestroy effectively never runs), and it lets the constructor tear
     * the chain down on logout instead of leaving it to fire later.
     */
    setAutoRefreshToken(): void {
      let token: string = this.store.selectSnapshot(AuthState.getAccessToken) || '';
      if (!token) return;
      if (!this.store.selectSnapshot(AuthState.isTokenExpired)) {
        let expirationDate: Date | null = null;
        try {
          const { exp } = jwtDecode<{ exp?: number }>(token);
          expirationDate = exp ? new Date(exp * 1000) : null;
        } catch {
          return;
        }
        if (expirationDate) {
          // 10 seconds reserved for requesting new tokens
          const expirationMilliseconds = new Date(expirationDate).getTime() - new Date().getTime() - 10 * 1000;
          this.refreshTimerSub?.unsubscribe();
          this.refreshTimerSub = timer(Math.max(expirationMilliseconds, 0))
            .pipe(switchMap(() => this.store.dispatch(new GenerateRefreshToken())))
            .subscribe({
              next: () => this.setAutoRefreshToken(),
              // Mirrors AuthInterceptor: a refresh that fails means the
              // refresh token is gone too, so send the user back through login.
              error: () => this.authService.redirectToLogin(),
            });
        }
      }
    }

  /**
   * Check if user is an employee based on TMS role (tools:employee)
   * @returns true if user has employee role from TMS
   */
  isEmployee(): boolean {
    return this.authService.isEmployeeUser();
  }
}
